import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import {
  CarouselButton,
  CarouselContent,
  CarouselProvider,
  Divider,
  Grid,
} from '@time-line/components/common';
import type { ReactNode } from 'react';
import type { ProductPage } from 'types/cms/pages/ProductPage';

import { useAvailableProducts } from '../../../hooks/product/useAvailableProducts';
import { ProductComplementaryProduct } from './ProductComplementaryProduct';

export interface ProductComplementaryProps {
  title?: string;
  label?: string;
  products: NonNullable<ProductPage['complementaryProducts']>['products'];
  ctaType?: 'addToCart';
  layout?: 'carousel' | 'stack';
  gridColumn?: BoxProps['gridColumn'];
  cardProps?: BoxProps;
}

const StackWrapper = ({ children }: { children: React.ReactNode }) => (
  <VStack gap="sm" alignItems="flex-start">
    {children}
  </VStack>
);

const CarouselWrapper = ({ children }: { children: ReactNode[] }) => (
  <CarouselContent
    breakpoints={{
      0: {
        slidesPerView: 1.15,
        spaceBetween: 12,
      },
      768: {
        slidesPerView: 1,
      },
    }}
    slidesPerView={1}
    loop={false}
    centeredSlides={false}
  >
    {children}
  </CarouselContent>
);

export function Inner({
  title,
  products,
  layout = 'stack',
  gridColumn = 'full',
  ...props
}: ProductComplementaryProps) {
  const ProductCards = layout === 'carousel' ? CarouselWrapper : StackWrapper;
  /* INFO: It is necessary to check this early to prevent empty Swiper slides */
  const { shopifyProducts, isProductAvailable } = useAvailableProducts(
    products.map(({ variant }) => variant.gid),
  );

  if (shopifyProducts.length === 0) return null;

  return (
    <Grid>
      <Divider mb="2xs" gridColumn={gridColumn} />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mb="2xs"
        gridColumn={gridColumn}
      >
        {title && <Text size="overline">{title}</Text>}
        {layout === 'carousel' && (
          <Flex gap="3xs">
            <CarouselButton
              direction="prev"
              _disabled={{
                pointerEvents: 'none',
                transitionDuration: 'slow',
                color: 'blackAlpha.500',
              }}
            />
            <CarouselButton
              direction="next"
              _disabled={{
                pointerEvents: 'none',
                transitionDuration: 'slow',
                color: 'blackAlpha.500',
              }}
            />
          </Flex>
        )}
      </Flex>

      <Box gridColumn="full">
        <ProductCards>
          {products.filter(isProductAvailable).map((product) => (
            <ProductComplementaryProduct
              {...product}
              {...props}
              key={product.slug}
            />
          ))}
        </ProductCards>
      </Box>
    </Grid>
  );
}

export function ProductComplementary({ ...props }: ProductComplementaryProps) {
  return (
    <CarouselProvider>
      <Inner {...props} />
    </CarouselProvider>
  );
}
